'use client';

import { usePathname, useRouter } from 'next/navigation';
import { JSX, PropsWithChildren, useCallback, useEffect, useRef } from 'react';

import { useAiToolkitContext } from '../_context/AiToolkitContext';
import { AI_TOOLKIT_TELEMETRY_ACTIONS, emitAiToolkitTag } from '../_context/aiToolkitTelemetry';
import { AI_TOOLKIT_CATEGORIES, AI_USER_SEGMENTS, AiToolkitCategory } from '../_context/types';
import { AiToolkitSidePanelActions } from './AiToolkitSidePanelActions';

export const CATEGORY_TO_CTA_INFO: Record<
    AiToolkitCategory,
    { label: string; upgradeLabel: string; subscribeLabel: string }
> = {
    [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]: {
        label: 'Generate Video',
        upgradeLabel: 'Upgrade to Generate',
        subscribeLabel: 'Subscribe to Generate',
    },
    [AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]: {
        label: 'Edit Video',
        upgradeLabel: 'Upgrade to Edit',
        subscribeLabel: 'Subscribe to Edit',
    },
    [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION]: {
        label: 'Generate Image',
        upgradeLabel: 'Upgrade to Generate',
        subscribeLabel: 'Subscribe to Generate',
    },
    [AI_TOOLKIT_CATEGORIES.VOICEOVER]: {
        label: 'Generate Voiceover',
        upgradeLabel: 'Upgrade to Generate',
        subscribeLabel: 'Subscribe to Generate',
    },
};

/**
 * Middle Column in 3 column layout. Typically shows a prompt box + a CTA button w/ credits (c.f. SidePanelSectionCTA.tsx).
 */

export interface AiToolkitSidePanelProps extends PropsWithChildren {
    aiToolkitCategory: AiToolkitCategory;
    onSubmit: () => void;
    isSubmitDisabled?: boolean;
}

export const AiToolkitSidePanel = ({
    children,
    aiToolkitCategory,
    onSubmit,
    isSubmitDisabled,
}: AiToolkitSidePanelProps): JSX.Element => {
    const { credits, categorySegments, subscriptionId, isEnterprise, dispatchConfirmationModal } =
        useAiToolkitContext();
    const pathname = usePathname();
    const router = useRouter();
    const scrollRef = useRef<HTMLDivElement>(null);
    const gradientRef = useRef<HTMLDivElement>(null);

    const updateGradient = useCallback((): void => {
        const el = scrollRef.current;
        const gradient = gradientRef.current;
        if (!el || !gradient) return;
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        gradient.style.opacity = String(Math.min(remaining / 80, 1));
    }, []);

    useEffect(() => {
        updateGradient();
        const el = scrollRef.current;
        if (!el) return;
        const observer = new ResizeObserver(updateGradient);
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateGradient]);

    const segment = categorySegments[aiToolkitCategory];
    const ctaInfo = CATEGORY_TO_CTA_INFO[aiToolkitCategory];
    const label =
        segment === AI_USER_SEGMENTS.SUBSCRIBED
            ? ctaInfo.label
            : segment === AI_USER_SEGMENTS.UPGRADABLE
              ? ctaInfo.upgradeLabel
              : ctaInfo.subscribeLabel;

    const handleSubmit = useCallback(
        (e: React.FormEvent<HTMLFormElement>): void => {
            e.preventDefault();
            if (segment === AI_USER_SEGMENTS.UNSUBSCRIBED) {
                emitAiToolkitTag(aiToolkitCategory, AI_TOOLKIT_TELEMETRY_ACTIONS.pricingRedirect);
                sessionStorage.setItem('aiToolReturnPath', pathname);
                router.push(`/pricing?redirectAfterRegister=${encodeURIComponent(pathname)}`);
                return;
            }
            if (segment === AI_USER_SEGMENTS.UPGRADABLE) {
                if (isEnterprise) {
                    dispatchConfirmationModal({
                        type: 'OPEN_MODAL',
                        modal: { type: 'entFeatureAccess', category: aiToolkitCategory },
                    });
                    return;
                }
                emitAiToolkitTag(aiToolkitCategory, AI_TOOLKIT_TELEMETRY_ACTIONS.purchaseCredits);
                router.push(
                    `/video/upgrade-my-plan/${subscriptionId}?redirectAfterRegister=${encodeURIComponent(pathname)}`,
                );
                return;
            }
            onSubmit();
        },
        [
            segment,
            subscriptionId,
            isEnterprise,
            dispatchConfirmationModal,
            aiToolkitCategory,
            onSubmit,
            pathname,
            router,
        ],
    );

    return (
        <form
            name={`${aiToolkitCategory}-form`}
            method="post"
            onSubmit={handleSubmit}
            className="scrollbar-thin-dark shrink-0 md:max-w-68 xl:max-w-121 w-full md:h-full flex flex-col justify-between border-t md:border-t-0 md:border-r dark:border-neutral-dark-200 md:max-h-[calc(100vh-132px)] topnav-md:max-h-[calc(100vh-67px)]"
        >
            <div className="w-full min-h-0 flex-1 relative">
                <div ref={scrollRef} onScroll={updateGradient} className="w-full p-5 pb-4 h-full overflow-y-auto">
                    {children}
                </div>
                <div
                    ref={gradientRef}
                    className="pointer-events-none absolute w-full h-14 bottom-0 bg-linear-to-b from-transparent to-gray-925 z-10"
                />
            </div>
            <div className="w-full flex flex-col gap-2">
                <AiToolkitSidePanelActions disabled={isSubmitDisabled} label={label} credits={credits} />
            </div>
        </form>
    );
};
